/**
 * HealthSection — renders the address + 3 array-valued health fields.
 *
 * Fields: address, chronicDiseases, drugAllergies, foodAllergies.
 *
 * Used by:
 *   - SignupPage  (step 2 of the wizard)
 *   - ProfilePage (middle section of the single-page edit form)
 *
 * The three array fields are all MUI `Autocomplete multiple + freeSolo`
 * with the SAME UX shape — only the suggestion list and label keys
 * differ. We loop over them via `renderArrayField`.
 *
 * The `autoSelect` prop is REQUIRED on these Autocompletes — it makes
 * MUI commit typed text to `field.value` when focus leaves the input,
 * not just on Enter. Without it, users who type "Penicillin" and then
 * click Next/Save lose their input silently because the text never
 * leaves the input's `inputValue` state. This was the Session 5
 * "missing allergies" bug.
 *
 * Props:
 *   - control  : react-hook-form `control` from useForm()
 *   - errors   : react-hook-form `formState.errors` from useForm()
 *   - t        : i18next translator
 *   - showHint : optional bool; when true, render the
 *                "health info is optional" hint above the array fields.
 *                On by default for signup; can be turned off on the
 *                edit page where it would feel redundant.
 */

import {
  Stack,
  TextField,
  Autocomplete,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';

import { CHRONIC_DISEASE_CODES, getDiseaseLabel } from './constants';

export default function HealthSection({ control, errors, t, showHint = true }) {
  /**
   * Render a multi-value Autocomplete (chronicDiseases, drugAllergies,
   * foodAllergies). All three share this shape; only their suggestion
   * list, label keys, and (for chronic diseases) per-option translator
   * differ.
   */
  const renderArrayField = ({
    name,
    labelKey,
    helperKey,
    placeholderKey,
    suggestions,
    translateOption,
  }) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          multiple
          freeSolo
          // See file-level docstring above for why autoSelect is required.
          autoSelect
          // handleHomeEndKeys lets Home/End move within the input text
          // rather than jumping inside the popup list — small a11y polish
          // that pairs naturally with freeSolo.
          handleHomeEndKeys
          options={suggestions}
          value={field.value || []}
          // Autocomplete passes (event, newValue, reason); we only care about newValue.
          onChange={(_event, newValue) => field.onChange(newValue)}
          getOptionLabel={(option) =>
            translateOption ? translateOption(option) : option
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                size="small"
                label={translateOption ? translateOption(option) : option}
                {...getTagProps({ index })}
                key={`${name}-${option}-${index}`}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(labelKey)}
              placeholder={
                (field.value || []).length === 0 ? t(placeholderKey) : ''
              }
              error={!!errors[name]}
              helperText={errors[name]?.message ?? t(helperKey)}
            />
          )}
        />
      )}
    />
  );

  return (
    <Stack spacing={2.5}>
      <Controller
        name="address"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('signup.fields.address.label')}
            placeholder={t('signup.fields.address.placeholder')}
            fullWidth
            multiline
            rows={3}
            autoComplete="street-address"
            error={!!errors.address}
            helperText={errors.address?.message ?? ' '}
          />
        )}
      />

      <Divider flexItem />

      {showHint && (
        <Typography variant="subtitle2" color="text.secondary">
          {t('signup.healthSectionHint')}
        </Typography>
      )}

      {renderArrayField({
        name: 'chronicDiseases',
        labelKey: 'signup.fields.chronicDiseases.label',
        helperKey: 'signup.fields.chronicDiseases.helper',
        placeholderKey: 'signup.fields.chronicDiseases.placeholder',
        suggestions: CHRONIC_DISEASE_CODES,
        translateOption: (code) => getDiseaseLabel(code, t),
      })}

      {renderArrayField({
        name: 'drugAllergies',
        labelKey: 'signup.fields.drugAllergies.label',
        helperKey: 'signup.fields.drugAllergies.helper',
        placeholderKey: 'signup.fields.drugAllergies.placeholder',
        suggestions: [],
        translateOption: null,
      })}

      {renderArrayField({
        name: 'foodAllergies',
        labelKey: 'signup.fields.foodAllergies.label',
        helperKey: 'signup.fields.foodAllergies.helper',
        placeholderKey: 'signup.fields.foodAllergies.placeholder',
        suggestions: [],
        translateOption: null,
      })}
    </Stack>
  );
}
